import { AsyncPipe, DOCUMENT, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { LoginContext } from '@shared/models';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import { AuthActions } from '@shared/store';
import { tap } from 'rxjs';
import {
  ActionTrackerService,
  Logger,
  MessagesComponent,
  NotificationMessage,
  ProcessingDirective,
} from 'thkee-common';
import { FirebaseAuthComponent } from '../firebase-auth/firebase-auth.component';
import { TurnstileComponent } from '../turnstile/turnstile.component';
const log = new Logger('Shared/app-form-login');

const loginErrors = {
  default: $localize`There are something wrong. Please try again!`,
  no_active_account: $localize`No active account found with the given credentials.`,
};

@UntilDestroy()
@Component({
  selector: 'app-form-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    NgTemplateOutlet,
    TurnstileComponent,
    FirebaseAuthComponent,
    AsyncPipe,
    ProcessingDirective,
    ReactiveFormsModule,
    FormsModule,
    FormlyModule,
    MessagesComponent,
  ],
})
export class FormLoginComponent implements OnInit, AfterContentChecked, OnDestroy, AfterViewInit {
  @Input() isTurnstileInPopup: boolean = false;
  private readonly TRACKER_ID = ActionTrackerService.uniqToken();
  messages = <NotificationMessage[]>[];
  loggingIn$ = this.actionTracker
    .isProcessing(this.TRACKER_ID)
    .pipe(tap((processing) => (processing ? this.form.disable() : this.form.enable())));
  readonly form = new FormGroup({});
  readonly fields: FormlyFieldConfig[] = [
    {
      key: 'email',
      type: 'input',
      props: {
        label: $localize`Email`,
        placeholder: $localize`Enter your email`,
        type: 'email',
        required: true,
      },
      validators: {
        validation: ['email'],
      },
    },
    {
      key: 'password',
      type: 'input-password',
      props: {
        label: $localize`Password`,
        placeholder: $localize`Enter your password`,
        required: true,
        type: 'password',
      },
    },
  ];

  captchaToken: string | null = null; // Store the captcha token

  constructor(
    private readonly store: Store,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private actionTracker: ActionTrackerService,
    private renderer: Renderer2,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.actionTracker
      .getFailed(this.TRACKER_ID)
      .pipe(untilDestroyed(this))
      .subscribe(({ metadata }) => {
        this.messages = (metadata?.messages || []).map((message) => {
          return {
            type: message.type,
            message: loginErrors[message.message as keyof typeof loginErrors] || loginErrors.default,
          };
        });
      });
    this.reserveRedirectPage();
  }

  // New auth
  direction: 'ltr' | 'rtl' = 'ltr';
  supertokensUrl = 'https://cdn.jsdelivr.net/gh/supertokens/prebuiltui@v0.48.0/build/static/js/main.81589a39.js';
  ngAfterViewInit() {
    const lang = this.document.documentElement.dir === 'ltr' ? 'en' : 'ar';
    this.loadScript(this.supertokensUrl, lang);
  }

  ngOnDestroy() {
    // Remove the script and CSS when the component is destroyed
    const script = this.document.getElementById('supertokens-script');
    if (script) {
      script.remove();
    }
  }

  private loadScript(src: string, language: 'en' | 'ar' = 'ar') {
    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.id = 'supertokens-script';
    script.onload = () => {
      this.authService.initSuperTokensUI(language);
    };
    this.renderer.appendChild(this.document.body, script);
  }
  // End new auth

  isAdminLogin: boolean = true;
  ngAfterContentChecked(): void {
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe((query) => {
      if (query['refresh'] && query['token'] && this.isAdminLogin) {
        this.store.dispatch(
          AuthActions.loginAsAdmin({ credentials: { refresh: query['refresh'], token: query['token'] } })
        );
        window.location.replace('/');
        this.isAdminLogin = false;
      }
    });
  }

  onLogin(): void {
    if (this.form.valid) {
      this.messages = [];
      this.store.dispatch(
        AuthActions.login({ data: { ...this.form.value, remember: true } as LoginContext, trackerId: this.TRACKER_ID })
      );
    }
  }

  private reserveRedirectPage() {
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe((query) => {
      if (query['redirect']) {
        this.authService.loginActionState.next({ openLoginModal: false, redirect: query['redirect'] });
      }
    });
  }

  // Captcha verified callback
  onCaptchaVerified(token: string): void {
    this.captchaToken = token;
  }

  // Captcha expired callback
  onCaptchaExpired(): void {
    this.captchaToken = null;
  }

  // NOTE: This method is for sing-in with google to get the CODE to send BE validation
  // which is not using for now
  // signInWithGoogle() {
  //   this.authService.signInWithGooglePopup();
  // }
}
